import { useCallback } from 'react';
import { ApiEmailAttachment, EnumPrintAttachmentKind, EnumPrintoutPart } from '@Model';
import { DOCUMENTS_API } from '@Constants/backendConstants';
import { ROOT_OBJECT_ATTACHMENT_CONFIG } from '@Hooks/Attachments/rootObjectAttachmentConfig';
import { useAttachmentDownloadUrl } from '@Hooks/Attachments/useAttachmentDownloadUrl';
import { EnumMessageLevel, resolveToBlobResponse, useToasts } from '@synqo/frontend-component-library';
import { useFileDownload } from '@Hooks/useFileDownload';
import { useMutation } from '@tanstack/react-query';
import { useDocumentsRepository } from '@Hooks/useRepository';
import { PrintOptionsRepository } from '@Repositories/Documents';

/**
 * Renders a download link for an email attachment, resolving its actual download URL only when
 * clicked rather than upfront — a raw attachment's URL is a short-lived, presigned link, so
 * minting it eagerly would risk it expiring before the user ever clicks it.
 */
export const DownloadableFile: React.FC<ApiEmailAttachment> = (attachment) => {
    if (attachment.kind === EnumPrintAttachmentKind.Raw) {
        return <RawAttachmentDownloadLink attachment={attachment} />;
    }
    return <PrintoutDownloadLink attachment={attachment} />;
};

/** The rendered printout itself, or its attachments merged into one PDF — never a presigned URL. */
function PrintoutDownloadLink({ attachment }: { attachment: ApiEmailAttachment }) {
    const { file_name, id, primary_id, part } = attachment;
    const relativeUrl = `print_options/${id}/${primary_id}${part === EnumPrintoutPart.Attachments ? '/attachments' : ''}`;

    const printOptions = useDocumentsRepository(PrintOptionsRepository);
    const { triggerDownload } = useFileDownload();
    const { pushToast } = useToasts();

    const { mutateAsync: download } = useMutation({
        mutationFn: async (): Promise<void> => {
            const [blob, response] =
                part === EnumPrintoutPart.Attachments
                    ? await printOptions.printAttachments<[Blob, Response]>(id, primary_id, resolveToBlobResponse)
                    : await printOptions.printDocument<[Blob, Response]>(id, primary_id, false, resolveToBlobResponse);

            if (!response.ok) {
                pushToast({
                    message: `Download von ${file_name} fehlgeschlagen.`,
                    title: 'Download',
                    level: EnumMessageLevel.Danger,
                });
                return;
            }

            triggerDownload(blob, response);
        },
    });

    const handleDownload = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            void download();
        },
        [download],
    );

    return (
        <div>
            <a href={`${DOCUMENTS_API}/${relativeUrl}`} onClick={handleDownload}>
                Download {`${file_name}`}
            </a>
        </div>
    );
}

/** A raw, previously uploaded attachment — its download URL is a presigned blob SAS link. */
function RawAttachmentDownloadLink({ attachment }: { attachment: ApiEmailAttachment }) {
    const { file_name, id, primary_id, attachment_id } = attachment;
    const config = ROOT_OBJECT_ATTACHMENT_CONFIG[id];
    const { mutateAsync: resolveDownloadUrl } = useAttachmentDownloadUrl(
        config?.baseUrl ?? '',
        config?.entityPath ?? '',
        primary_id,
    );
    const { triggerUrlDownload } = useFileDownload();

    const handleDownload = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            if (!config) {
                return;
            }
            void resolveDownloadUrl(attachment_id)
                .then(triggerUrlDownload)
                .catch(() => undefined);
        },
        [config, resolveDownloadUrl, triggerUrlDownload, attachment_id],
    );

    if (!config) {
        return null;
    }

    return (
        <div>
            <a href="#" onClick={handleDownload}>
                Download {`${file_name}`}
            </a>
        </div>
    );
}
